/**
 * Per-column status options editor (Monday-style).
 *
 * Lists the status options for a Status custom column: each row has a drag
 * handle (reorder — the row order here IS the dropdown order), a colour swatch
 * (click → palette popover incl. a free custom colour picker), a label input,
 * and a delete button. "Add option" appends a blank row. Save commits all
 * changes to the column via `onSave`; Cancel discards. Empty labels are
 * dropped on save.
 */
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Input, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { FormDialog } from '@/shared/ui/FormDialog';
import { DragHandle } from '@/shared/ui';
import { STATUS_COLOR_PALETTE, type StatusOption } from '../../lib/useTrackersBoard';
import { useTranslation } from 'react-i18next';

interface StatusConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current status options for the column being configured. */
  options: StatusOption[];
  /** Called with the new option list when the user saves. */
  onSave: (next: StatusOption[]) => void;
  /**
   * Dialog title. The same editor doubles as the board's custom-group
   * manager (Group by: Custom) — groups share the option shape exactly
   * (label + color + order).
   */
  title?: string;
}

function makeOptionId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `so_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function SortableOptionRow({
  option,
  onUpdate,
  onRemove,
}: {
  option: StatusOption;
  onUpdate: (id: string, patch: Partial<StatusOption>) => void;
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation('trackers');

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 ${isDragging ? 'z-10 opacity-70' : ''}`}
    >
      {/* Drag handle — row order here defines the dropdown order. */}
      <DragHandle
        ariaLabel={t('trackersBoard.statusConfigModal.reorderOption', {
          label: option.label || t('trackersBoard.statusConfigModal.option'),
        })}
        attributes={attributes}
        listeners={listeners}
        className="flex size-7 shrink-0 items-center justify-center transition-colors duration-fast ease-out hover:bg-accent hover:text-foreground"
      />
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t('trackersBoard.statusConfigModal.pickColour')}
            className="size-7 shrink-0 rounded-md border border-border transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{ backgroundColor: option.color }}
          />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="grid grid-cols-5 gap-1.5">
            {STATUS_COLOR_PALETTE.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={t('trackersBoard.statusConfigModal.setColourTo', { color })}
                onClick={() => onUpdate(option.id, { color })}
                className={`size-6 rounded-md transition-transform hover:scale-110 ${
                  option.color === color
                    ? 'ring-2 ring-foreground ring-offset-1 ring-offset-background'
                    : ''
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          {/* Free colour picker — for any specific colour beyond the presets.
             The native input is transparent and overlaid on a labelled row so
             the whole row is clickable and shows the current colour. */}
          <div className="mt-2 flex cursor-pointer items-center gap-2 rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
            <span
              className="size-4 shrink-0 rounded-sm border border-border"
              style={{ backgroundColor: option.color }}
            />
            <label htmlFor={`custom-color-${option.id}`} className="flex-1 cursor-pointer">
              {t('trackersBoard.statusConfigModal.customColor')}
            </label>
            <input
              id={`custom-color-${option.id}`}
              type="color"
              aria-label={t('trackersBoard.statusConfigModal.pickACustomColour')}
              value={option.color}
              onChange={(e) => onUpdate(option.id, { color: e.target.value })}
              className="absolute h-0 w-0 opacity-0"
            />
          </div>
        </PopoverContent>
      </Popover>
      <Input
        value={option.label}
        onChange={(e) => onUpdate(option.id, { label: e.target.value })}
        placeholder={t('trackersBoard.statusConfigModal.optionLabel')}
        className="flex-1"
      />
      <button
        type="button"
        aria-label={t('trackersBoard.statusConfigModal.deleteOption')}
        onClick={() => onRemove(option.id)}
        className="flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Trash2 className="size-4" aria-hidden />
      </button>
    </div>
  );
}

export function StatusConfigModal(props: StatusConfigModalProps) {
  const { t } = useTranslation('trackers');

  const {
    open,
    onOpenChange,
    options,
    onSave,
    title = t('trackersBoard.statusConfigModal.configureStatusOptions'),
  } = props;

  // Local draft — committed only on Save. Re-seeded each time the modal opens
  // so re-opens always start from the current persisted options.
  const [draft, setDraft] = useState<StatusOption[]>(options);
  useEffect(() => {
    if (open) setDraft(options);
  }, [open, options]);

  const canSave = useMemo(() => draft.every((o) => o.label.trim().length > 0), [draft]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const draftIds = useMemo(() => draft.map((o) => o.id), [draft]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((prev) => {
      const from = prev.findIndex((o) => o.id === active.id);
      const to = prev.findIndex((o) => o.id === over.id);
      if (from < 0 || to < 0) return prev;
      return arrayMove(prev, from, to);
    });
  };

  const updateOption = (id: string, patch: Partial<StatusOption>) => {
    setDraft((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  };

  const removeOption = (id: string) => {
    setDraft((prev) => prev.filter((o) => o.id !== id));
  };

  const addOption = () => {
    setDraft((prev) => [
      ...prev,
      { id: makeOptionId(), label: '', color: STATUS_COLOR_PALETTE[0] },
    ]);
  };

  const handleSave = () => {
    // Strip empty-label rows so saving doesn't pollute the column.
    onSave(draft.filter((o) => o.label.trim().length > 0));
    onOpenChange(false);
  };

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={title}>
      <FormDialog.Body>
        <div className="flex flex-col gap-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            autoScroll={false}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={draftIds} strategy={verticalListSortingStrategy}>
              {draft.map((option) => (
                <SortableOptionRow
                  key={option.id}
                  option={option}
                  onUpdate={updateOption}
                  onRemove={removeOption}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addOption}
            className="w-fit gap-1.5"
          >
            <Plus className="size-4" aria-hidden />
            {t('trackersBoard.statusConfigModal.addOption')}
          </Button>
        </div>
      </FormDialog.Body>
      <FormDialog.Footer
        secondary={{
          label: t('trackersBoard.statusConfigModal.cancel'),
          onClick: () => onOpenChange(false),
        }}
        primary={{
          label: t('trackersBoard.statusConfigModal.save'),
          onClick: handleSave,
          disabled: !canSave,
        }}
      />
    </FormDialog>
  );
}
